import React, { useState } from "react";
import { FaDownload, FaEye, FaTrash } from "react-icons/fa";
import { buildProtectedUploadUrl } from "../utils/secureFileAccess";

const UploadedAttachmentList = ({
  title = "Uploaded Files",
  files = [],
  getFileKey,
  getFileName,
  getFileViewUrl,
  getFileDownloadUrl,
  getFileDownloadName,
  getFileMeta,
  canDelete = false,
  onDeleteFile,
  deletingFileId = null,
}) => {
  const [activeActionKey, setActiveActionKey] = useState(null);
  const actionButtonStyle = {
    width: "34px",
    height: "34px",
    padding: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    flex: "0 0 34px",
  };

  if (!files.length) return null;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFileBlob = async (url) => {
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`File request failed with status ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const errorPayload = await response.json().catch(() => null);
      throw new Error(errorPayload?.message || "File endpoint returned JSON instead of a file");
    }

    return response.blob();
  };

  const openBlobInNewTab = (blob) => {
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
  };

  const downloadBlob = (blob, filename) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };

  const handleFileAction = async ({ actionKey, url, mode, filename }) => {
    if (!url) return;

    try {
      setActiveActionKey(actionKey);
      const blob = await fetchFileBlob(url);

      if (mode === "view") {
        openBlobInNewTab(blob);
        return;
      }

      downloadBlob(blob, filename);
    } catch (err) {
      console.error(`Failed to ${mode} file`, err);
      window.alert(`Failed to ${mode} file. Please try again.`);
    } finally {
      setActiveActionKey(null);
    }
  };

  return (
    <div className="mt-4 p-3 bg-light rounded border">
      <div className="text-muted mb-2">{title}</div>
      <ul className="list-unstyled mb-0">
        {files.map((file, index) => {
          const fileKey = getFileKey(file, index);
          const fileName = getFileName(file, index);
          const rawViewUrl = getFileViewUrl(file, index);
          const viewUrl = buildProtectedUploadUrl(rawViewUrl, {
            mode: "view",
            filename: fileName,
          });
          const rawDownloadUrl = getFileDownloadUrl
            ? getFileDownloadUrl(file, index)
            : rawViewUrl;
          const downloadName = getFileDownloadName
            ? getFileDownloadName(file, index)
            : fileName;
          const downloadUrl = buildProtectedUploadUrl(rawDownloadUrl, {
            mode: "download",
            filename: downloadName,
          });
          const fileMeta = getFileMeta ? getFileMeta(file, index) : null;
          const isDeleting = deletingFileId === fileKey;
          const isViewing = activeActionKey === `view:${fileKey}`;
          const isDownloading = activeActionKey === `download:${fileKey}`;

          return (
            <li
              key={fileKey}
              className="d-flex justify-content-between align-items-center gap-3 py-2 border-top"
            >
              <div className="flex-grow-1">
                <div className="fw-semibold text-break">{fileName}</div>
                {fileMeta ? <div className="text-muted small">{fileMeta}</div> : null}
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {viewUrl ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={actionButtonStyle}
                    title="View"
                    onClick={() =>
                      handleFileAction({
                        actionKey: `view:${fileKey}`,
                        url: viewUrl,
                        mode: "view",
                      })
                    }
                    disabled={Boolean(activeActionKey)}
                  >
                    {isViewing ? "..." : <FaEye />}
                  </button>
                ) : null}

                {downloadUrl ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={actionButtonStyle}
                    title="Download"
                    onClick={() =>
                      handleFileAction({
                        actionKey: `download:${fileKey}`,
                        url: downloadUrl,
                        mode: "download",
                        filename: downloadName,
                      })
                    }
                    disabled={Boolean(activeActionKey)}
                  >
                    {isDownloading ? "..." : <FaDownload />}
                  </button>
                ) : null}

                {canDelete && onDeleteFile ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    style={actionButtonStyle}
                    onClick={() => onDeleteFile(file)}
                    disabled={isDeleting}
                    title="Delete"
                  >
                    {isDeleting ? "..." : <FaTrash />}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default UploadedAttachmentList;
